import { GiftPassPreviewPriceBasis, type GiftPassBundlePreviewQuery } from '@bonsai/shared';
import { useMemo } from 'react';

import { GiftPassBundleApiError } from '../../../api/gift-pass-bundles';
import { useDebouncedValue } from '../../../hooks/use-debounced-value';
import { useGiftPassBundlePreview } from '../use-gift-pass-bundles';

import { previewTerms, type BundleFormValues } from './bundle-form';

const PREVIEW_DELAY_MS = 300;

function failureMessage(error: unknown): string {
  if (error instanceof GiftPassBundleApiError && error.status === 503) {
    return 'The pricing settings are misconfigured, so today’s prices can’t be shown.';
  }
  return 'Today’s prices couldn’t be loaded. Try again in a moment.';
}

export function useBundleCostPreview(values: BundleFormValues) {
  // Debounced as text: a fresh object every render would restart the timer forever.
  const current = JSON.stringify(previewTerms(values));
  const settled = useDebouncedValue(current, PREVIEW_DELAY_MS);
  const terms = useMemo(() => JSON.parse(settled) as GiftPassBundlePreviewQuery | null, [settled]);
  const query = useGiftPassBundlePreview(terms);

  const ready = current !== 'null';
  const upToDate = ready && current === settled && !query.isPlaceholderData && !query.isFetching;
  const preview = ready ? query.data : undefined;
  const fallback = preview?.prices.find((p) => p.basis === GiftPassPreviewPriceBasis.FALLBACK);

  return {
    ready,
    preview,
    /** False while the prices shown are for terms the form no longer has. */
    upToDate,
    /** The bundle at the fallback price, only when it is for the terms on screen. */
    atFallback: (upToDate && fallback) || null,
    failure: ready && query.isError ? failureMessage(query.error) : null,
  };
}
